import { useMemo } from 'react';
import dayjs from 'dayjs';
import useReportLog from '../../../../hooks/useReportLog';
import useGroup from '../../../../zustand/useGroup';
import useAppliedReportFilter from '../../../../hooks/useAppliedReportFilter';
import ReportChart from '../reportChart';
import {
  AE_RUNNING_TIME_Y_AXIS,
  buildAeRunningTimeTable,
  enhanceAeLogs,
  getAggregatedLength,
  PdfChartData,
  selectAeSchema,
} from '../../pdf/reportPdfData';
import styled from 'styled-components';
import { formatChartTables } from './chartTableHeader';

interface IProps {
  isReportSchedule?: {
    startAt: number;
    endAt: number;
    interval: any;
    deviceType: string;
    assetId: string;
    timezone: number;
  };
  height?: number;
  compact?: boolean;
  // calculated by the backend for the PDF page (GET /report-log/pdf-data)
  precomputed?: PdfChartData;
}

const ChartAERunningTime: React.FC<IProps> = ({
  isReportSchedule,
  height,
  compact,
  precomputed,
}) => {
  const { logs } = useReportLog();
  const { filter, timezone } = useAppliedReportFilter();
  const reportTimezone = isReportSchedule?.timezone ?? timezone;

  const enhancedLogs = useMemo(
    () => (precomputed ? logs : enhanceAeLogs(logs)),
    [precomputed, logs]
  );

  const dataSchema = useMemo(
    () => (precomputed ? precomputed.schema : selectAeSchema(enhancedLogs)),
    [precomputed, enhancedLogs]
  );

  const dataTable = useMemo(() => {
    if (precomputed) {
      return precomputed.table;
    }

    return buildAeRunningTimeTable({
      enhancedLogs,
      schema: dataSchema,
      schedule: isReportSchedule,
      timezone: reportTimezone,
    });
  }, [precomputed, isReportSchedule, enhancedLogs, dataSchema, reportTimezone]);

  const tables = useMemo(
    () =>
      formatChartTables(
        dataTable,
        isReportSchedule?.startAt as number,
        reportTimezone
      ),
    [dataTable, isReportSchedule?.startAt, reportTimezone]
  );

  const tableColumns = useMemo(() => {
    let res: any[] = ['Time'];
    dataSchema.forEach((item) => {
      if (!item.dataKeys.includes('avgFuelCons')) {
        res.push(item.seriesOption.name);
      }
    });

    return res;
  }, [dataSchema]);

  const aggregatedLength = useMemo(() => {
    if (isReportSchedule) {
      return getAggregatedLength({
        startAt: isReportSchedule.startAt,
        endAt: isReportSchedule.endAt,
        interval: isReportSchedule.interval,
      });
    } else {
      return getAggregatedLength({
        startAt: filter.startAt,
        endAt: filter.endAt,
        interval: filter.interval,
      });
    }
  }, [filter, isReportSchedule]);

  return (
    <>
      <ReportChart
        height={height}
        logs={enhancedLogs}
        filter={isReportSchedule || filter}
        yAxis={AE_RUNNING_TIME_Y_AXIS}
        chartSchema={dataSchema}
        isReportSchedule={
          isReportSchedule && isReportSchedule.deviceType ? true : false
        }
        aggregatedLength={aggregatedLength}
        sumRunningTime
        stacking
        compact={compact}
        precomputedSeries={precomputed?.series}
      />

      {isReportSchedule && tableColumns.length && (
        <>
          {tables.map((table, tableIdx) => (
            <div key={tableIdx} style={{ marginTop: tableIdx > 0 ? '14px' : '0' }}>
              {dataTable.is48HourMode && isReportSchedule && (
                <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '7px', marginBottom: '2px' }}>
                  {dayjs(isReportSchedule.startAt * 1000)
                    .utcOffset(reportTimezone * 60)
                    .add(tableIdx, 'day')
                    .format('DD MMM YYYY')}
                </div>
              )}
              <TableSummary columnCount={dataTable.columnCount} is48HourMode={dataTable.is48HourMode}>
                <tbody>
                  {table.map((data, idx) => (
                    <tr key={idx}>
                      <td>{tableColumns[idx]}</td>
                      {data.map((item: number | string | undefined, idx2: number) => (
                        <td key={idx2}>{typeof item === 'undefined' || item === null ? '-' : item}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </TableSummary>
            </div>
          ))}
        </>
      )}
    </>
  );
};

const TableSummary = styled.table<{ columnCount: number; is48HourMode: boolean }>`
  width: 100%;
  margin: ${props => props.is48HourMode ? '5px auto 0' : '0 auto'};
  table-layout: fixed;
  font-size: ${props => props.is48HourMode ? '6px' : props.columnCount > 10 ? '8px' : '10px'};
  td {
    border: thin solid #ddd;
    padding: ${props => props.is48HourMode ? '1px' : props.columnCount > 10 ? '4px 1px' : '6px 2px'};
    font-size: inherit;
    text-align: center;
  }
  tr:first-child {
    background: #d1d1d1;
  }
  td:first-child {
    width: ${props => props.is48HourMode ? '45px' : props.columnCount > 10 ? '60px' : '80px'};
    background: #142d79;
    color: white;
    border-color: #142d79;
  }
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin: 20px 0 10px 0;
  padding: 8px;
  background: #f5f5f5;
  border-left: 4px solid #142d79;
`;

export default ChartAERunningTime;
